
import React, { useState } from 'react';
import { 
  ref, 
  set, 
  serverTimestamp 
} from 'firebase/database';

const MorningHabits = ({
  user,
  database,
  userHabits,
  habitTracking,
  showToast,
  getISTTime
}) => {
  const [newHabitName, setNewHabitName] = useState('');
  const [showHabitNotePopup, setShowHabitNotePopup] = useState(null);
  const [habitNote, setHabitNote] = useState('');
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [suggestionPage, setSuggestionPage] = useState(0);
  const suggestionsPerPage = 5;

  const goToPreviousPage = () => {
    setSuggestionPage((prev) => Math.max(0, prev - 1));
  };

  const goToNextPage = () => {
    setSuggestionPage((prev) => Math.min(totalPages - 1, prev + 1));
  };

  // Habit suggestions (2-minute friendly)
  const habitSuggestions = [
    '1-minute breathing', '2-minute meditation', 'Quick stretches', 'Two-minute plank', 'Light jogging in place',
    'Read one page', 'Write one gratitude line', 'Drink a glass of water', 'Make bed neat', 'Open the windows',
    'Sunlight stretch', 'Neck rolls', 'Shoulder rolls', '2-minute body scan', 'Posture reset',
    'Smile practice', 'Affirmation aloud', 'Tongue stretch', 'Cold splash to face', 'Quick tidy up',
    'Two-minute yoga flow', 'Desk stretches', 'Calf raises', 'Arm circles', 'Mindful sipping',
    '5 deep breaths', 'Back stretch', 'Toe touches', 'Foot roll', 'Hand massage',
    'Eye palming', 'Gratitude list line', 'Clean one surface', 'Laundry fold burst', 'Make a quick plan',
    'Read a quote', 'Say hello to a neighbor', 'Sort mail stack', 'Wipe sink', 'Prep a healthy snack',
    'Water plants', 'Quick push-ups', 'Two-minute journaling', 'Stretch quads', 'Check posture',
    'Practice balance', 'Practice a language flashcard', 'Do a mirror pep talk', 'Listen to a song snippet', 'Quick sketch'
  ];

  const totalPages = Math.ceil(habitSuggestions.length / suggestionsPerPage);
  const visibleSuggestions = habitSuggestions.slice(
    suggestionPage * suggestionsPerPage,
    suggestionPage * suggestionsPerPage + suggestionsPerPage
  );

  // Add a new habit from suggestion and return id
  const addHabitFromSuggestion = async (habitName) => {
    if (!user) return null;

    try {
      const habitId = Date.now().toString();
      const habitRef = ref(database, `userHabits/${user.uid}/${habitId}`);
      await set(habitRef, {
        name: habitName,
        createdAt: serverTimestamp(),
        durationMinutes: 2,
      });
      showToast(`${habitName} added to your habits!`, 'success');
      return habitId;
    } catch (error) {
      console.error('Error adding habit:', error);
      showToast('Error adding habit. Please try again.', 'error');
      return null;
    }
  };

  const addHabit = async () => {
    if (!user) return;
    if (!newHabitName.trim()) {
      showToast('Please enter a habit name', 'error');
      return;
    }

    try {
      const habitId = Date.now().toString();
      const habitRef = ref(database, `userHabits/${user.uid}/${habitId}`);
      await set(habitRef, {
        name: newHabitName.trim(),
        createdAt: serverTimestamp()
      });
      setNewHabitName('');
      showToast('Habit added successfully!', 'success');
    } catch (error) {
      console.error('Error adding habit:', error);
      showToast('Error adding habit. Please try again.', 'error');
    }
  };

  // Mark habit for today
  const markHabitToday = (habitId) => {
    if (!user) return;
    setShowHabitNotePopup(habitId);
    setHabitNote('');
  };

  const submitHabitWithNote = async () => {
    if (!user || !showHabitNotePopup) return;

    const today = getISTTime().toISOString().slice(0, 10);
    try {
      const trackingRef = ref(database, `habitTracking/${user.uid}/${showHabitNotePopup}/${today}`);
      const noteData = habitNote.trim() ? { completed: true, note: habitNote.trim() } : true;
      await set(trackingRef, noteData);
      showToast('Habit marked! Keep it up! 🎉', 'success');
      setShowHabitNotePopup(null);
      setHabitNote('');
    } catch (error) {
      console.error('Error marking habit:', error);
      showToast('Error marking habit. Please try again.', 'error');
    }
  };

  // Delete habit
  const deleteHabit = async (habitId) => {
    if (!user) return;
    if (!window.confirm('Are you sure you want to delete this habit?')) return;

    try {
      const habitRef = ref(database, `userHabits/${user.uid}/${habitId}`);
      const trackingRef = ref(database, `habitTracking/${user.uid}/${habitId}`);
      await set(habitRef, null);
      await set(trackingRef, null);
      showToast('Habit deleted', 'success');
    } catch (error) {
      console.error('Error deleting habit:', error);
      showToast('Error deleting habit. Please try again.', 'error');
    }
  };

  const getHabitStatusForLast7Days = (habitId) => {
    const tracking = habitTracking[habitId] || {};
    const today = getISTTime();
    const status = [];

    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().slice(0, 10);
      const entry = tracking[dateStr];
      const done = Boolean(entry && (entry === true || entry.completed === true));
      status.push(done);
    }

    return status;
  };

  const getHabitCurrentStreak = (habitId) => {
    const tracking = habitTracking[habitId] || {};
    const today = getISTTime();
    let streak = 0;

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().slice(0, 10);
      const entry = tracking[dateStr];
      const done = Boolean(entry && (entry === true || entry.completed === true));
      if (!done) break;
      streak += 1;
    }

    return streak;
  };

  const getHabitBestStreak = (habitId) => {
    const tracking = habitTracking[habitId] || {};
    const sortedDates = Object.keys(tracking)
      .filter((dateStr) => !!dateStr)
      .sort();

    let best = 0;
    let current = 0;

    sortedDates.forEach((dateStr) => {
      const entry = tracking[dateStr];
      const done = Boolean(entry && (entry === true || entry.completed === true));
      if (done) {
        current += 1;
        best = Math.max(best, current);
      } else {
        current = 0;
      }
    });

    return best;
  };

  const getHabitIcon = (habitName) => {
    const normalized = habitName.toLowerCase();
    if (normalized.includes('meditation') || normalized.includes('yoga')) return '🧘';
    if (normalized.includes('exercise') || normalized.includes('workout') || normalized.includes('run')) return '🏃';
    if (normalized.includes('read') || normalized.includes('book') || normalized.includes('study')) return '📖';
    if (normalized.includes('journal') || normalized.includes('journaling')) return '✍️';
    if (normalized.includes('water') || normalized.includes('drink')) return '💧';
    if (normalized.includes('shower') || normalized.includes('cold')) return '❄️';
    return habitName.charAt(0).toUpperCase();
  };

  const isHabitMarkedToday = (habitId) => {
    const today = getISTTime().toISOString().slice(0, 10);
    const tracking = habitTracking[habitId] || {};
    const entry = tracking[today];
    return Boolean(entry && (entry === true || entry.completed === true));
  };

  const getHabitConsistency = (habitId) => {
    const tracking = habitTracking[habitId] || {};
    return Object.values(tracking).filter((entry) => Boolean(entry && (entry === true || entry.completed === true))).length;
  };

  return (
    <div className="habits-page">
      <div className="habits-header">
        <div>
          <h1>2-Minute Morning Habits ✨</h1>
          <p className="subtitle">Small daily actions create big changes.</p>
        </div>
        <div className="add-row">
          <input
            type="text"
            placeholder="✣ Enter a new habit, e.g., Meditation, Exercise, Read"
            value={newHabitName}
            onChange={(e) => setNewHabitName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                addHabit();
              }
            }}
            className="input habit-input"
          />
          <button onClick={addHabit} className="add-btn">＋ Add Habit</button>
        </div>
      </div>

      <div className="suggestions-section">
        <div className="suggestions-header">
          <div>
            <h2>Quick 2-minute habit ideas</h2>
            <p>Swipe through 50 short habit cards and tap any for details.</p>
          </div>
          <div className="slider-controls">
            <button
              type="button"
              className="slider-arrow"
              onClick={goToPreviousPage}
              aria-label="Previous page"
              disabled={suggestionPage === 0}
            >
              ‹
            </button>
            <span className="slider-page-indicator">Page {suggestionPage + 1} / {totalPages}</span>
            <button
              type="button"
              className="slider-arrow"
              onClick={goToNextPage}
              aria-label="Next page"
              disabled={suggestionPage === totalPages - 1}
            >
              ›
            </button>
          </div>
        </div>
        <div className="suggestion-slider">
          {visibleSuggestions.map((suggestion, idx) => (
            <div
              key={`${suggestion}-${suggestionPage}-${idx}`}
              className="suggestion-card"
              onClick={() => setSelectedSuggestion({ name: suggestion, index: suggestionPage * suggestionsPerPage + idx })}
            >
              <div className="suggestion-title">{suggestion}</div>
              <div className="suggestion-mini">Tap to view details</div>
            </div>
          ))}
        </div>
      </div>
      {Object.keys(userHabits).length > 0 ? (
        <div className="habit-list">
          {Object.entries(userHabits).map(([habitId, habit]) => {
            const isMarkedToday = isHabitMarkedToday(habitId);
            const consistency = getHabitConsistency(habitId);
            const currentStreak = getHabitCurrentStreak(habitId);
            const bestStreak = getHabitBestStreak(habitId);
            const weekStatus = getHabitStatusForLast7Days(habitId);
            const icon = getHabitIcon(habit.name);

            return (
              <article key={habitId} className="habit-card">
                <div className="habit-icon">{icon}</div>
                <div className="habit-main">
                  <div className="habit-title">
                    {habit.name}
                    <span className="tag">{consistency} days</span>
                  </div>
                  <div className="habit-desc">Track consistency, stay focused, and lock in morning momentum.</div>
                  <div className="best">Best streak: {bestStreak} day{bestStreak === 1 ? '' : 's'}</div>
                  <div className="week">
                    {['M','T','W','T','F','S','S'].map((dayLetter, index) => (
                      <div key={`${dayLetter}-${index}`} className="day">
                        <span>{dayLetter}</span>
                        <span className={`dot ${weekStatus[index] ? 'done' : ''}`}>{weekStatus[index] ? '✓' : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="habit-side">
                  <div className="habit-actions">
                    <button
                      onClick={() => markHabitToday(habitId)}
                      className={`mark-btn ${isMarkedToday ? 'done' : ''}`}
                      disabled={isMarkedToday}
                      title={isMarkedToday ? 'Already marked today' : 'Mark habit today'}
                    >
                      {isMarkedToday ? 'Done Today' : '◎ Mark Today'}
                    </button>
                    <button
                      onClick={() => deleteHabit(habitId)}
                      className="menu"
                      title="Delete habit"
                    >
                      ⋮
                    </button>
                  </div>
                  <div className="stats">
                    <div>Streak<strong>{currentStreak}</strong></div>
                    <div>Best<strong>{bestStreak}</strong></div>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-habits">
          <i className="fas fa-lightbulb"></i>
          <p>No habits yet. Start building your morning routine!</p>
          <p className="empty-hint">Add your first habit to get started</p>
        </div>
      )}

      {Object.keys(userHabits).length > 0 && <div className="view">View All Habits →</div>}

      {showHabitNotePopup && (
        <div className="modal-overlay" onClick={() => setShowHabitNotePopup(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close" 
              onClick={() => setShowHabitNotePopup(null)}
            >
              ×
            </button>
            <div className="modal-header">
              <h3>Mark Habit Complete</h3>
            </div>
            <div className="modal-body">
              <p>Add a note (optional):</p>
              <textarea
                className="habit-note-input"
                placeholder="How did the habit go today?"
                value={habitNote}
                onChange={(e) => setHabitNote(e.target.value)}
                rows="4"
              />
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setShowHabitNotePopup(null)}>Cancel</button>
              <button className="submit-btn" onClick={submitHabitWithNote}>Mark Complete</button>
            </div>
          </div>
        </div>
      )}

      {selectedSuggestion && (
        <div className="modal-overlay" onClick={() => setSelectedSuggestion(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedSuggestion(null)}>×</button>
            <div className="modal-header">
              <h3>{selectedSuggestion.name}</h3>
            </div>
            <div className="modal-body">
              <p>This is a 2-minute habit: "{selectedSuggestion.name}". It's quick, focused, and great for morning momentum.</p>
              <p>Duration: 2 minutes</p>
            </div>
            <div className="modal-footer">
              <button className="cancel-btn" onClick={() => setSelectedSuggestion(null)}>Close</button>
              <button className="submit-btn" onClick={async () => {
                const id = await addHabitFromSuggestion(selectedSuggestion.name);
                if (id) {
                  // mark today as done immediately
                  const today = getISTTime().toISOString().slice(0,10);
                  const trackingRef = ref(database, `habitTracking/${user.uid}/${id}/${today}`);
                  await set(trackingRef, true);
                  showToast('Added and marked done for today!', 'success');
                }
                setSelectedSuggestion(null);
              }}>Do Now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MorningHabits;
